import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  DenseTable,
  StatusBadge,
  defaultNumberFormat,
  type Column,
} from "../../components/index.ts";
import type { TraceSummary } from "./use-traces.ts";
import { TraceLabels } from "./TraceLabels.tsx";
import {
  spanStatusLabel,
  spanStatusLevel,
  spanStatusTitle,
  traceDetailHref,
} from "./vocab.ts";

/**
 * The trace list (task 11.11; design 12.2 "trace list … across services"; 12.3 drill-down; 12.4
 * semantic table). It renders one page of `GET /api/v1/traces` summaries through the shared semantic
 * `DenseTable` — a real `<table>` with a caption and column headers, so the list is fully readable
 * by assistive tech without the waterfall (design 12.4).
 *
 * Each row carries, independently, the trace's `inferred` and `partial` labels via {@link TraceLabels}
 * (the two flags are shown as two separate badges, both when both apply, neither taking precedence),
 * the root span name, the services the trace touched, its start, duration, worst span status
 * (icon+text, never colour alone) and span count. The root-span-name cell is the DRILL: clicking it
 * navigates to the trace's waterfall at `/traces/{id}` (design 12.3 trace row → waterfall).
 *
 * Rows are ordered most-recent-first (by `started_at`), so the newest traces are at the top; the
 * caller has already fetched the page, this component only presents it.
 */
export function TraceList(props: { traces: readonly TraceSummary[] }): ReactNode {
  const navigate = useNavigate();

  const sorted = [...props.traces].sort(
    (a, b) => (b.started_at ?? 0) - (a.started_at ?? 0),
  );

  const columns: readonly Column<TraceSummary>[] = [
    {
      key: "root",
      header: "Trace",
      isRowHeader: true,
      cell: (t) => (
        <button
          type="button"
          className="trc-link"
          onClick={() => navigate(traceDetailHref(t.trace_id))}
          aria-label={`Open the waterfall for trace ${t.root_span_name} (${t.trace_id})`}
          title={t.trace_id}
        >
          <span className="trc-link__name">{t.root_span_name}</span>
          <code className="trc-link__id">{shortId(t.trace_id)}</code>
        </button>
      ),
    },
    {
      key: "labels",
      header: "Labels",
      // The two INDEPENDENT inferred/partial badges; both show when both apply, neither dominates.
      cell: (t) => <TraceLabels trace={t} context={t.trace_id} />,
    },
    {
      key: "status",
      header: "Status",
      cell: (t) => (
        <StatusBadge
          level={spanStatusLevel(t.worst_status)}
          label={spanStatusLabel(t.worst_status)}
          size="sm"
          title={spanStatusTitle(t.worst_status)}
        />
      ),
    },
    {
      key: "services",
      header: "Services",
      cell: (t) =>
        t.services.length === 0 ? (
          <span className="trc-faint">n/a</span>
        ) : (
          <span className="trc-services" title={t.services.join(", ")}>
            {t.services.join(", ")}
          </span>
        ),
    },
    {
      key: "spans",
      header: "Spans",
      align: "right",
      cell: (t) => <span className="trc-num">{defaultNumberFormat(t.span_count)}</span>,
    },
    {
      key: "duration",
      header: "Duration",
      align: "right",
      cell: (t) => (
        <span className="trc-num" title={`${t.duration_ms} ms`}>
          {formatMs(t.duration_ms)}
        </span>
      ),
    },
    {
      key: "started",
      header: "Started",
      align: "right",
      cell: (t) => (
        <time
          className="trc-num"
          dateTime={isoAttr(t.started_at)}
          title={isoAttr(t.started_at)}
        >
          {formatTimeOfDay(t.started_at)}
        </time>
      ),
    },
  ];

  return (
    <DenseTable
      caption="Traces in this window, newest first. Each trace's inferred and partial labels are independent; open a trace to see its cross-service waterfall."
      columns={columns}
      rows={sorted}
      rowKey={(t) => t.trace_id}
      stickyHeader
      emptyMessage="No traces in this window."
    />
  );
}

/** Format a millisecond duration for display (`123 ms` / `1.23 s`), matching the Waterfall default. */
function formatMs(ms: number): string {
  if (!Number.isFinite(ms)) return "n/a";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

/** A short, readable form of a long trace id for the row (full id is the row's title/aria). */
function shortId(id: string): string {
  return id.length <= 12 ? id : `${id.slice(0, 8)}…`;
}
